import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { DefaultRootState } from '@reduxLocal/persistState';
import { clearReactQueryCachesAfterSessionContextChange } from '@utils/clearReactQueryCachesAfterSessionContextChange';

const sessionContextKey = (
  selectedProfile: { id?: string; _id?: string } | null | undefined,
  userId?: string | null,
) => {
  const companyId = selectedProfile?.id || selectedProfile?._id;
  if (companyId) return `company:${companyId}`;
  return `personal:${userId || ''}`;
};

/**
 * Watches company / personal context and resets session-scoped queries
 * (projects, datasets, calendars, knowledge, messaging) when it changes.
 */
export const useCompanyQueryInvalidation = () => {
  const queryClient = useQueryClient();
  const selectedProfile = useSelector(
    (state: DefaultRootState) => state.app.selectedProfile
  );
  const userId = useSelector(
    (state: DefaultRootState) => state.promiseData.data?.userData?.id
  );
  const contextKey = sessionContextKey(selectedProfile, userId);

  const prevContextKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const prevContextKey = prevContextKeyRef.current;
    if (prevContextKey === undefined) {
      prevContextKeyRef.current = contextKey;
      return;
    }

    if (prevContextKey !== contextKey) {
      clearReactQueryCachesAfterSessionContextChange(queryClient);
      prevContextKeyRef.current = contextKey;
    }
  }, [contextKey, queryClient]);
};

export default useCompanyQueryInvalidation;
